import { useMemo } from "react";

import { useAtom } from "jotai";
import { ArrowDownUpIcon, UsersIcon } from "lucide-react";

import { getAovColumns } from "src/components/Analytics/AovAnalytics/aovColumns";
import persistentAovAtom from "src/components/Analytics/AovAnalytics/persistentAovAtom";
import { KpiCard } from "src/components/Analytics/KpiCard/KpiCard";
import { DataChart } from "src/components/dataView/DataChart/DataChart";
import { DataTable } from "src/components/dataView/DataTable/DataTable";
import DataTableViewSettings from "src/components/dataView/DataTable/DataTableViewSettings";
import { colorGradient } from "src/components/dataView/DataTable/tableHelpers";
import { MetricsFilter } from "src/components/dataView/MetricsTable/MetricsFilter";
import { MetricsNumberInput } from "src/components/dataView/MetricsTable/MetricsNumberInput";
import { DateRangePicker } from "src/components/DateRangePicker/DateRangePicker";
import DashboardLayout from "src/layouts/DashboardLayout/DashboardLayout";
import { aovAtom, useAnalytics } from "src/lib/hooks/useAnalytics";
import { AOV_ANALYTICS_QUERY } from "src/lib/queries/analytics";

const dummyBuckets = Array.from({ length: 20 }, (_, i) => i + 1);

const AovAnalytics = () => {
  const [persistentStore, setPersistentStore] = useAtom(persistentAovAtom);
  const analytics = useAnalytics(
    aovAtom,
    AOV_ANALYTICS_QUERY,
    "aovAnalytics",
    ({ data }) => {
      const { __typename, ...rest } = data;

      return rest;
    }
  );
  const { store, refreshData, numberFormat } = analytics;
  const aovColumns = useMemo(() => getAovColumns(numberFormat), [numberFormat]);

  const visibleMetrics = useMemo(
    () =>
      aovColumns.filter(
        (col) =>
          persistentStore.viewOptions.find((o) => o.name === col.title)?.value
      ),
    [persistentStore.viewOptions, aovColumns]
  );

  const columns = useMemo(() => {
    return [
      {
        header: "Bucket",
        accessorKey: "bucket",
        meta: {
          className: "min-w-[100px] text-left",
          sticky: true,
          rightBorder: true,
        },
      },
      ...visibleMetrics.map((metric) => ({
        id: metric.field,
        header: metric.title,
        accessorKey: metric.field,
        meta: {
          className: "min-w-[110px]",
          format: metric.format,
        },
        cell: metric.color
          ? colorGradient({
              colors: [metric.color],
              asBadge: true,
              className: "justify-end",
              format: metric.format || numberFormat.whole,
            })
          : ({ getValue }) => (
              <div className="text-right">
                {(metric.format || numberFormat.whole)(getValue())}
              </div>
            ),
      })),
    ];
  }, [visibleMetrics, numberFormat]);

  const tableData = useMemo(() => {
    const buckets = (store.data?.buckets || dummyBuckets).map((b) =>
      parseFloat(b)
    );
    if (!buckets.length) return [];

    const rows = [];
    for (let i = 0; i < buckets.length; i++) {
      const prev = i === 0 ? 0 : buckets[i - 1];
      const curr = buckets[i];

      const row = {
        id: `b-${i}`,
        bucket: `${numberFormat.currency(prev, 0)} - ${numberFormat.currency(curr, 0)}`,
      };

      // attach each visible metric value for this bucket index
      visibleMetrics.forEach((m) => {
        const series = store.data?.[m.field] || [];
        row[m.field] = series[i] ?? 0;
      });

      rows.push(row);
    }
    return rows;
  }, [store.data, visibleMetrics, numberFormat]);

  // Loading skeleton rows (keeps widths stable)
  const loadingData = useMemo(() => {
    const length = store.data?.buckets?.length || dummyBuckets.length;

    return Array.from({ length }, (_, i) => {
      const row = { id: `loading-${i}`, bucket: " " };

      visibleMetrics.forEach((m) => (row[m.field] = undefined));
      return row;
    });
  }, [store.data?.buckets, visibleMetrics]);

  const chartData = useMemo(() => {
    if (!store.data?.buckets || !store.data?.distribution) return [];

    return store.data.buckets.map((bucket, index) => {
      const previousBucket = store.data.buckets[index - 1] || 0;
      let bucketRange;

      if (index === 0) {
        bucketRange = `≤${numberFormat.currency(bucket, 0)}`;
      } else if (index === store.data.buckets.length - 1) {
        bucketRange = `${numberFormat.currency(previousBucket, 0)}+`;
      } else {
        bucketRange = `${numberFormat.currency(previousBucket, 0)}-${numberFormat.currency(bucket, 0)}`;
      }

      return {
        bucket: bucketRange,
        distribution: store.data.distribution[index] * 100,
        repeatCustomerRate: store.data.repeatCustomerRate[index] * 100,
      };
    });
  }, [store.data, numberFormat]);

  const thresholdValue = useMemo(() => {
    const threshold = store.suggestedFreeShippingThreshold;
    if (!threshold || !store.data?.buckets?.length) return null;

    for (let i = 0; i < store.data.buckets.length; i++) {
      const prev = parseFloat(store.data.buckets[i - 1] ?? "0");
      const current = parseFloat(store.data.buckets[i]);

      if (threshold > prev && threshold <= current) {
        return `${numberFormat.currency(prev)} - ${numberFormat.currency(current)}`;
      }
    }

    return null;
  }, [store.data.buckets, store.suggestedFreeShippingThreshold, numberFormat]);

  const repeatCustomerRates = store.data.repeatCustomerRate;
  const buckets = store.data.buckets?.map((b) => parseFloat(b));

  // Find index of max repeat customer rate
  let maxIdx = 0;
  for (let i = 1; i < repeatCustomerRates?.length; i++) {
    if (repeatCustomerRates[i] > repeatCustomerRates[maxIdx]) {
      maxIdx = i;
    }
  }

  // Find corresponding bucket
  const highestRepeatCustomerBucket = buckets?.[maxIdx];

  return (
    <>
      <DashboardLayout.Header onRefresh={refreshData} loading={store.loading} />
      <DashboardLayout.Main>
        <DashboardLayout.Filters>
          <DateRangePicker atom={aovAtom} onSubmit={refreshData} />

          <MetricsNumberInput
            title="Increment:"
            icon={ArrowDownUpIcon}
            defaultValue={store.suggestedIncrement}
            formatFn={numberFormat.currency}
            field="increment"
            atom={aovAtom}
            queryVariableMapping={(value) => value || store.suggestedIncrement}
            onSubmit={refreshData}
            disabled={store.loading}
          />

          <MetricsFilter
            title="Customer type"
            icon={UsersIcon}
            options={[
              { label: "New", value: false },
              { label: "Returning", value: true },
            ]}
            atom={aovAtom}
            field="returningCustomer"
            onSubmit={refreshData}
            queryVariableMapping={(value) => {
              if (value?.length === 1) {
                return value[0] === true;
              }

              return undefined;
            }}
            disabled={store.loading}
          />
        </DashboardLayout.Filters>

        <div className="grid gap-8 @2xl:grid-cols-[.666fr_auto]">
          <div className="@container">
            <DashboardLayout.Cards className="relative">
              <DataTable
                columns={columns}
                data={tableData}
                atom={aovAtom}
                className="max-w-fit"
                loadingData={loadingData}
                alternateRowColors
              />

              <div className="absolute top-1 -left-4 z-20 sm:-left-6">
                <DataTableViewSettings
                  options={persistentStore.viewOptions}
                  onOptionChange={(option, value) => {
                    setPersistentStore((prev) => {
                      const viewOptions = prev.viewOptions.map((o) => {
                        if (o.name === option.name) {
                          return { ...o, value };
                        }
                        return o;
                      });
                      return { ...prev, viewOptions };
                    });
                  }}
                  onReset={() => {
                    setPersistentStore((prev) => ({
                      ...prev,
                      viewOptions: aovColumns.map((col) => ({
                        name: col.title,
                        value: col.viewByDefault,
                      })),
                    }));
                  }}
                  isColumns
                />
              </div>
            </DashboardLayout.Cards>
          </div>

          <div className="@container">
            <DashboardLayout.Cards className="sticky top-16 @md:grid-cols-2">
              <KpiCard
                title="Free Shipping Threshold"
                description="Suggested free shipping threshold based on AOV"
                loading={store.loading}
                value={store.suggestedFreeShippingThreshold}
                format={numberFormat.currency}
              />
              <KpiCard
                title="Highest Repeat Customer Bucket"
                loading={store.loading}
                value={highestRepeatCustomerBucket}
                format={numberFormat.currency}
              />

              <DataChart
                className="@md:col-span-2"
                data={chartData}
                loading={store.loading}
                dateFrom={store.dateFrom}
                dateTo={store.dateTo}
                type="bar"
                xAxisDataKey="bucket"
                referenceLine={thresholdValue}
                chartConfig={{
                  distribution: {
                    type: "bar",
                    yAxis: { id: "left" },
                    color: "var(--chart-1)",
                    label: "Distribution",
                    toggleable: false,
                  },
                  repeatCustomerRate: {
                    type: "area",
                    yAxis: { id: "right" },
                    color: "var(--chart-3)",
                    label: "Repeat Customer Rate",
                    shortLabel: "Repeat Rate",
                    toggleable: true,
                    defaultToggled: false,
                  },
                }}
                title="Average Order Value (AOV) Distribution"
                valueFormatter={(value) => numberFormat.percent(value)}
              />
            </DashboardLayout.Cards>
          </div>
        </div>
      </DashboardLayout.Main>
    </>
  );
};

export default AovAnalytics;
